import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { type ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import {
	isSSEMessage,
	type ParsedSSEMessage,
	type StreamMessage,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { exponentialRetry } from '@atlassian/conversation-assistant-service/common/utils/exponential-retry';
import type { Conversation } from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { StreamErrorException } from './error-classes';
import { createReconnectionMetrics, isRetryableReconnectionError } from './reconnect-action';
import type { ChatStoreContainerProps } from './types';

const MAX_RECONNECT_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;
const RECONNECT_JITTER_MS = 500;
const POST_ONLINE_DELAY_MS = 2000;
const RECONNECT_ABORT_REASON_CHAT_UNMOUNTED = 'chat-unmounted';

export interface ReconnectableStreamOptions<T> {
	initialGenerator: AsyncGenerator<T>;

	/**
	 * Predicate evaluated on each disconnect. Designed to close over mutable
	 * state (e.g. agentMessageId, isReconnectSupported) that is discovered
	 * during iteration, so it always reflects the latest values.
	 */
	canReconnect: () => boolean;

	/**
	 * Acquires a fresh generator from the reconnect SSE endpoint.
	 * Receives the last successfully processed event ID for resumption
	 * via the Last-Event-ID header.
	 */
	getReconnectGenerator: (lastEventId: string | undefined) => Promise<AsyncGenerator<T>>;

	/**
	 * Extracts the SSE event ID from a chunk for Last-Event-ID tracking.
	 * Return undefined for non-SSE chunks.
	 */
	getEventId: (chunk: T) => string | undefined;

	/**
	 * Called once before the first reconnect attempt.
	 * Use for one-time setup such as aligning temporary client IDs
	 * with server-assigned IDs.
	 */
	onBeforeFirstReconnect?: () => void;

	/** Called before each reconnect attempt with the current attempt number and last event ID. */
	onReconnecting?: (attempt: number, maxAttempts: number, lastEventId: string | undefined) => void;

	/** Called when a new generator is successfully acquired after reconnection. */
	onReconnected?: () => void;

	/** Called on the first chunk yielded after any reconnection. */
	onFirstChunkAfterReconnect?: () => void;

	/** Called when the stream completes normally after at least one reconnection. */
	onStreamCompleted?: () => void;

	/** Called when the stream fails with an error after at least one reconnection. */
	onStreamFailed?: (error: unknown) => void;

	/** AbortSignal to cancel reconnection during backoff waits. */
	signal?: AbortSignal;

	maxAttempts?: number;

	/**
	 * Optional predicate checked before each retry. Return false to skip
	 * reconnection (e.g. when the chat component has unmounted and lazy
	 * reconnection should take over instead).
	 */
	shouldContinueRetrying?: () => boolean;
}

/**
 * Wraps an async generator with transparent reconnection on client-side
 * streaming errors. The consumer iterates the returned generator exactly
 * like a raw one — reconnection is an implementation detail.
 *
 * On disconnect:
 * 1. Evaluates `canReconnect()` — if false, rethrows.
 * 2. Calls `onBeforeFirstReconnect()` once for one-time ID alignment.
 * 3. Acquires a new generator via `getReconnectGenerator(lastEventId)`
 *    with exponential backoff.
 * 4. Resumes yielding chunks from the new generator.
 *
 * Server-side errors (StreamErrorException) are never retried.
 */
export async function* createReconnectableStream<T>({
	initialGenerator,
	canReconnect,
	getReconnectGenerator,
	getEventId,
	onBeforeFirstReconnect,
	onReconnecting,
	onReconnected,
	onFirstChunkAfterReconnect,
	onStreamCompleted,
	onStreamFailed,
	signal,
	maxAttempts = MAX_RECONNECT_ATTEMPTS,
	shouldContinueRetrying,
}: ReconnectableStreamOptions<T>): AsyncGenerator<T> {
	let currentGenerator = initialGenerator;
	let lastEventId: string | undefined;
	let reconnectCount = 0;
	let reconnectionAttempted = false;
	let awaitingFirstChunkAfterReconnect = false;

	while (true) {
		try {
			for await (const chunk of currentGenerator) {
				if (signal?.aborted) {
					return;
				}

				const eventId = getEventId(chunk);
				if (eventId) {
					lastEventId = eventId;
				}

				if (awaitingFirstChunkAfterReconnect) {
					awaitingFirstChunkAfterReconnect = false;
					onFirstChunkAfterReconnect?.();
				}

				yield chunk;
			}

			if (reconnectionAttempted) {
				onStreamCompleted?.();
			}
			return;
		} catch (error) {
			if (error instanceof StreamErrorException) {
				if (reconnectionAttempted) {
					onStreamFailed?.(error);
				}
				throw error;
			}

			if (
				!isRetryableReconnectionError(error) ||
				!canReconnect() ||
				reconnectCount >= maxAttempts ||
				signal?.aborted
			) {
				if (reconnectionAttempted) {
					onStreamFailed?.(error);
				}
				throw error;
			}

			if (shouldContinueRetrying && !shouldContinueRetrying()) {
				if (reconnectionAttempted) {
					onStreamFailed?.(error);
				}
				throw error;
			}

			if (!reconnectionAttempted) {
				onBeforeFirstReconnect?.();
				reconnectionAttempted = true;
			}

			reconnectCount++;
			onReconnecting?.(reconnectCount, maxAttempts, lastEventId);

			currentGenerator = await exponentialRetry({
				fn: async () => getReconnectGenerator(lastEventId),
				maxRetries: 2,
				delay: RETRY_BASE_DELAY_MS,
				jitter: RECONNECT_JITTER_MS,
				pauseWhileOffline: true,
				postOnlineDelay: POST_ONLINE_DELAY_MS,
				errorFilter: isRetryableReconnectionError,
				signal,
			});

			onReconnected?.();
			awaitingFirstChunkAfterReconnect = true;
		}
	}
}

// ---------------------------------------------------------------------------
// Domain-specific factory
// ---------------------------------------------------------------------------

type SSEStreamChunk = StreamMessage | ParsedSSEMessage<StreamMessage>;

export interface ChatReconnectableStreamOptions {
	generator: AsyncGenerator<SSEStreamChunk>;
	conversationId: string;
	abortController: AbortController;

	assistanceService: ChatStoreContainerProps['assistanceService'];
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
	experienceTracker: ExperienceTrackerAPI;

	/**
	 * Getter for the server-assigned message ID. Returns undefined before
	 * the ConversationChannelDataMessage chunk is processed, which naturally
	 * prevents reconnection until the server ID is known.
	 */
	getMessageId: () => string | undefined;

	canReconnect: () => boolean;
	updateCurrentConversation: (data: Partial<Conversation>) => void;
	getIsChatMounted?: () => boolean;

	/** Extra attributes spread into every analytics/experience event and perf metrics. */
	analyticsAttributes?: Record<string, unknown>;

	/** One-time setup before first reconnect (e.g. ID alignment). */
	onBeforeFirstReconnect?: () => void;

	/**
	 * Call-site-specific hooks composed on top of the built-in
	 * analytics/metrics handling.
	 */
	onReconnecting?: (attempt: number, maxAttempts: number) => void;
	onReconnected?: () => void;
	onFirstChunkAfterReconnect?: () => void;
}

/**
 * Creates a reconnectable stream pre-wired with Rovo chat conventions:
 * - Reconnects via `assistanceService.reconnectToMessageStream`
 * - Extracts SSE event IDs from `ParsedSSEMessage` chunks
 * - Fires `rovoChatReconnection` analytics on each retry
 * - Tracks RECONNECT_TO_ACTIVE_STREAM experience (start/succeed/fail/abort)
 * - Tracks reconnection performance metrics (timeToConnect, timeToResume)
 * - Updates conversation traces with retry status
 * - Pauses reconnection when chat is unmounted
 *
 * Callers pass hooks for site-specific concerns (dead air tracking,
 * inactivity watchdogs, ID alignment, MAU events).
 */
export function createChatReconnectableStream({
	generator,
	conversationId,
	abortController,
	assistanceService,
	sendAnalyticsEvent,
	experienceTracker,
	getMessageId,
	canReconnect,
	updateCurrentConversation,
	getIsChatMounted,
	analyticsAttributes,
	onBeforeFirstReconnect: callerOnBeforeFirstReconnect,
	onReconnecting: callerOnReconnecting,
	onReconnected: callerOnReconnected,
	onFirstChunkAfterReconnect: callerOnFirstChunkAfterReconnect,
}: ChatReconnectableStreamOptions): AsyncGenerator<SSEStreamChunk> {
	let totalAttempts = 0;
	let perfMetrics: ReturnType<typeof createReconnectionMetrics> | undefined;

	return createReconnectableStream<SSEStreamChunk>({
		initialGenerator: generator,

		canReconnect: () => !!getMessageId() && canReconnect(),

		getReconnectGenerator: (lastEventId) => {
			const messageId = getMessageId();
			if (!messageId) {
				return Promise.reject(new Error('Cannot reconnect: messageId is not available'));
			}
			return assistanceService
				.reconnectToMessageStream({
					conversationId,
					messageId,
					lastEventId,
					controller: abortController,
				})
				.then((r: { generator: AsyncGenerator<SSEStreamChunk> }) => r.generator);
		},

		getEventId: (chunk) => (isSSEMessage(chunk) && chunk.id ? chunk.id : undefined),

		onBeforeFirstReconnect: () => {
			perfMetrics = createReconnectionMetrics({
				isDeepResearchEnabled: analyticsAttributes?.isDeepResearch as boolean | undefined,
				thinkDeeperEnabled: analyticsAttributes?.thinkDeeperEnabled as boolean | undefined,
				fastModeEnabled: analyticsAttributes?.fastModeEnabled as boolean | undefined,
			});
			perfMetrics.timeToConnectToStreamMetrics.start();
			experienceTracker.start({
				name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
				id: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
				attributes: {
					conversationId,
					messageId: getMessageId(),
					...analyticsAttributes,
				},
			});
			callerOnBeforeFirstReconnect?.();
		},

		onReconnecting: (attempt, maxAttempts, lastEventId) => {
			totalAttempts = attempt;
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'retrying',
				actionSubject: 'rovoChatReconnection',
				attributes: {
					conversationId,
					messageId: getMessageId(),
					attempt,
					maxAttempts,
					hasLastEventId: !!lastEventId,
					...analyticsAttributes,
				},
			});
			updateCurrentConversation({
				traces: [
					{
						messageContent: `Connection lost. Retrying... (${attempt}/${maxAttempts})`,
						status: 'loading',
					},
				],
			});
			callerOnReconnecting?.(attempt, maxAttempts);
		},

		onReconnected: () => {
			perfMetrics?.timeToConnectToStreamMetrics.stop();
			perfMetrics?.timeToResumeStreamMetrics.start();
			callerOnReconnected?.();
		},

		onFirstChunkAfterReconnect: () => {
			perfMetrics?.timeToResumeStreamMetrics.stop();
			callerOnFirstChunkAfterReconnect?.();
		},

		onStreamCompleted: () => {
			experienceTracker.succeed({
				name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
				attributes: {
					totalAttempts,
					conversationId,
					messageId: getMessageId(),
					...analyticsAttributes,
				},
			});
		},

		onStreamFailed: (error) => {
			perfMetrics?.timeToConnectToStreamMetrics.cancel();
			perfMetrics?.timeToResumeStreamMetrics.cancel();

			if (
				(error instanceof Error && error.name === 'AbortError') ||
				abortController.signal.aborted
			) {
				experienceTracker.abort({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					reason: 'Aborted',
				});
			} else {
				experienceTracker.fail({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					error: error instanceof Error ? error : new Error(String(error)),
					attributes: {
						totalAttempts,
						conversationId,
						messageId: getMessageId(),
						...analyticsAttributes,
					},
				});
			}
		},

		shouldContinueRetrying: getIsChatMounted
			? () => {
					if (!getIsChatMounted()) {
						abortController.abort(RECONNECT_ABORT_REASON_CHAT_UNMOUNTED);
						return false;
					}
					return true;
				}
			: undefined,

		signal: abortController.signal,
	});
}
